import { parseCookies } from 'nookies';
import { useEffect } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';

import { path } from 'f';

import { getMixpanelDistinctId } from 'lib/analytics/mixpanel-helpers';
import { getCurrentUserId } from 'lib/auth';
import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';
import * as Select from 'lib/select';

import { useTalkPageContext } from 'components/pages/talks/slug/talk-page-context';
import { VideoData } from 'lib/pages/talks/slug/[...slug].props';
import { useRouter } from 'next/router';
import useVideoPlayerStore from '../../store';
import { usePlayerTalkData } from './usePlayerTalkData';

export const useSetup = (video: VideoData) => {
  const router = useRouter();
  const autoplayQuery = router.query.autoplay;
  const mutedQuery = router.query.muted;
  const { autoplay, muted, t } = useTalkPageContext();
  const {
    onService,
    onTalk,
    ready,
    onSeek,
    autoplayed,
    onCurrentTime,
    isEmbed,
    setShouldAutoplay
  } = useVideoPlayerStore(state => ({
    ...state
  }));

  const shouldAutoplay = isEmbed
    ? autoplayQuery === 'true'
    : autoplay || autoplayed;

  useEffect(() => {
    setShouldAutoplay(Boolean(shouldAutoplay));
  }, [autoplay, setShouldAutoplay, shouldAutoplay]);

  const shouldMute = isEmbed ? mutedQuery === 'true' : muted;
  const mixpanelDistinctId = getMixpanelDistinctId();
  const userId = parseCookies()._ted_user_id || getCurrentUserId();
  const isMobileDevice = isAndroid || isIOS;
  const isMobileWeb = useIsBreakpointWidth({
    size: 'sm',
    breakPointType: 'tui'
  });
  const isMobile = isMobileDevice || isMobileWeb;

  const playerTalkData = usePlayerTalkData(video);

  // Ensure we have valid playerTalkData before accessing properties
  const service = playerTalkData ? Select.service(playerTalkData) : 'internal';
  const youtubeID = playerTalkData
    ? path(['external', 'code'])(playerTalkData)
    : null;

  useEffect(() => {
    if (video) {
      // Determine if internal or youtube player is being used and set the service
      onService(video);
      onTalk(video);
      onCurrentTime(0);
    }
  }, [onCurrentTime, onService, onTalk, video]);

  useEffect(() => {
    if (t && ready) {
      // If the player is ready and the start time is set, seek to the start time
      onSeek(t);
    }
  }, [ready, t, onSeek]);

  return {
    isMobile,
    shouldAutoplay,
    mixpanelDistinctId,
    userId,
    playerTalkData,
    service,
    youtubeID,
    startTime: t,
    muted: shouldMute
  };
};
